// Service calendar — every machine's upcoming services on one month view.
// Calendar-interval services have real due dates; hour-interval services are
// projected from how fast each machine accumulates hours.
import { useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { api } from '../lib/api.js';

const DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const pad = n => String(n).padStart(2, '0');
const ymd = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const monthKey = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
const parseYmd = s => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
const longDate = s => parseYmd(s).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });

export default function ServiceCalendar() {
  const { canManageEquipment } = useOutletContext();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [selected, setSelected] = useState(null);   // 'YYYY-MM-DD' | null

  async function load() {
    try { setData(await api.get('/api/calendar?months=12')); }
    catch (e) { setErr(e.message); }
  }
  useEffect(() => { load(); }, []);

  const today = ymd(new Date());
  const thisMonth = useMemo(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); }, []);
  const lastMonth = useMemo(() => new Date(thisMonth.getFullYear(), thisMonth.getMonth() + 11, 1), [thisMonth]);

  const byDate = useMemo(() => {
    const map = {};
    for (const o of data?.occurrences || []) (map[o.date] ||= []).push(o);
    return map;
  }, [data]);

  if (err) return <div className="text-rust-700 text-sm">{err}</div>;
  if (!data) return <div className="text-ink-700/60 italic">Loading…</div>;

  const overdue = data.occurrences.filter(o => o.overdue);
  const inMonth = data.occurrences.filter(o => o.date.startsWith(monthKey(month)) && !o.overdue);
  const agenda = selected ? (byDate[selected] || []).filter(o => !o.overdue) : inMonth;
  const agendaDates = [...new Set(agenda.map(o => o.date))];

  // Month grid cells: leading blanks, then each day
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells = [...Array(first.getDay()).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  function go(delta) {
    const next = new Date(month.getFullYear(), month.getMonth() + delta, 1);
    if (next < thisMonth || next > lastMonth) return;
    setMonth(next); setSelected(null);
  }

  const nothingYet = data.machines.length === 0;

  return (
    <div className="space-y-5">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-serif text-olive-900">Service calendar</h1>
          <p className="text-sm text-ink-700/70">Upcoming maintenance across every machine.</p>
        </div>
        <Link to="/equipment" className="btn-ghost text-sm">Equipment ›</Link>
      </header>

      {nothingYet && (
        <div className="card text-sm text-ink-700/70">
          No service schedules yet. Add a machine, upload its manual, and approve the extracted schedule — its services will show up here.
        </div>
      )}

      {overdue.length > 0 && (
        <section className="card border-l-4 border-rust-500 space-y-2">
          <h2 className="font-medium text-rust-700">Due now ({overdue.length})</h2>
          <ul className="space-y-1">
            {overdue.map(o => <Occurrence key={`${o.task_id}-${o.seq}`} o={o} />)}
          </ul>
        </section>
      )}

      {!nothingYet && (
        <section className="card space-y-3">
          <div className="flex items-center justify-between">
            <button onClick={() => go(-1)} disabled={month <= thisMonth} className="btn-ghost text-sm disabled:opacity-30" aria-label="Previous month">‹</button>
            <h2 className="font-medium text-olive-900">{month.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}</h2>
            <button onClick={() => go(1)} disabled={month >= lastMonth} className="btn-ghost text-sm disabled:opacity-30" aria-label="Next month">›</button>
          </div>

          <div className="grid grid-cols-7 gap-1 text-center">
            {DOW.map((d, i) => <div key={i} className="text-[10px] uppercase tracking-wider text-ink-700/50 pb-1">{d}</div>)}
            {cells.map((day, i) => {
              if (day == null) return <div key={i} />;
              const key = `${monthKey(month)}-${pad(day)}`;
              const items = byDate[key] || [];
              const isToday = key === today;
              const isSel = key === selected;
              const hasOverdue = items.some(o => o.overdue);
              const firm = items.filter(o => o.seq === 0 && !o.overdue).length;
              const repeats = items.filter(o => o.seq > 0).length;
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => setSelected(isSel ? null : key)}
                  disabled={items.length === 0}
                  aria-label={`${longDate(key)}: ${items.length} service${items.length === 1 ? '' : 's'}`}
                  className={`rounded-lg py-1.5 min-h-[3rem] flex flex-col items-center gap-1 transition
                    ${isSel ? 'bg-olive-700 text-cream-50' : items.length ? 'bg-olive-50 hover:bg-olive-100 text-ink-900' : 'text-ink-700/40'}
                    ${isToday && !isSel ? 'ring-2 ring-gold-500' : ''}`}
                >
                  <span className={`text-sm ${isToday ? 'font-bold' : ''}`}>{day}</span>
                  {items.length > 0 && (
                    <span className="flex items-center gap-0.5">
                      {hasOverdue && <Dot className="bg-rust-500" />}
                      {firm > 0 && <Dot className={isSel ? 'bg-cream-50' : 'bg-olive-700'} />}
                      {repeats > 0 && <Dot className={isSel ? 'bg-cream-50/50' : 'bg-olive-700/35'} />}
                      {items.length > 1 && <span className={`text-[10px] leading-none ml-0.5 ${isSel ? 'text-cream-50/80' : 'text-ink-700/60'}`}>{items.length}</span>}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-3 flex-wrap text-xs text-ink-700/60">
            <span className="inline-flex items-center gap-1"><Dot className="bg-rust-500" /> due now</span>
            <span className="inline-flex items-center gap-1"><Dot className="bg-olive-700" /> next service</span>
            <span className="inline-flex items-center gap-1"><Dot className="bg-olive-700/35" /> later repeat</span>
          </div>

          <div className="border-t border-olive-100/60 pt-3 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs uppercase tracking-wider text-ink-700/60">
                {selected ? longDate(selected) : `All of ${month.toLocaleDateString(undefined, { month: 'long' })}`}
              </h3>
              {selected && <button onClick={() => setSelected(null)} className="text-xs text-olive-700 hover:underline">show whole month</button>}
            </div>
            {agenda.length === 0 ? (
              <p className="text-sm text-ink-700/60 italic">Nothing scheduled{selected ? ' this day' : ' this month'}.</p>
            ) : agendaDates.map(d => (
              <div key={d} className="space-y-1">
                {!selected && <div className="text-xs font-medium text-ink-700">{longDate(d)}</div>}
                <ul className="space-y-1">
                  {agenda.filter(o => o.date === d).map(o => <Occurrence key={`${o.task_id}-${o.seq}`} o={o} />)}
                </ul>
              </div>
            ))}
          </div>
        </section>
      )}

      <UsageEstimates machines={data.machines} unscheduled={data.unscheduled} canManage={canManageEquipment} onSaved={load} />

      {!nothingYet && <PhoneFeed />}
    </div>
  );
}

function Dot({ className }) {
  return <span className={`inline-block h-1.5 w-1.5 rounded-full ${className}`} />;
}

function Occurrence({ o }) {
  const repeat = o.seq > 0;
  const detail = [
    o.basis === 'hours' && o.due_hours != null ? `at ${o.due_hours} hr` : null,
    o.basis === 'date' && o.interval_days ? `every ${o.interval_days} d` : null,
    o.basis === 'hours' && o.interval_hours ? `every ${o.interval_hours} hr` : null,
    o.projected && !o.overdue ? (repeat ? 'later repeat · estimated date' : 'estimated date') : null,
  ].filter(Boolean).join(' · ');
  return (
    <li className={`border-l-2 pl-3 py-1 ${o.overdue ? 'border-rust-500' : repeat ? 'border-olive-100' : 'border-olive-700'}`}>
      <Link to={`/equipment/${o.equipment_id}`} className={`text-sm hover:underline ${repeat ? 'text-ink-700/70' : 'text-ink-900'}`}>
        <span className="font-medium">{o.equipment}</span> — {o.description}
      </Link>
      {detail && <div className="text-xs text-ink-700/60">{detail}</div>}
    </li>
  );
}

// Hour-based services can't be dated until we know roughly how much a machine runs.
function UsageEstimates({ machines, unscheduled, canManage, onSaved }) {
  const need = machines.filter(m => m.needs_estimate);
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState('');
  if (need.length === 0) return null;

  async function save(m) {
    const v = Number(vals[m.id]);
    if (!v || v <= 0) return;
    setBusy(m.id); setErr('');
    try {
      await api.patch(`/api/equipment/${m.id}`, { est_hours_per_week: v });
      await onSaved();
    } catch (e) { setErr(e.message); }
    finally { setBusy(null); }
  }

  return (
    <section className="card space-y-3">
      <div>
        <h2 className="font-medium text-olive-900">Not on the calendar yet</h2>
        <p className="text-sm text-ink-700/70">
          These machines have services due by hour-meter reading, but there isn't enough hour-meter history to guess a date.
          {canManage ? ' Enter roughly how many hours a week each one runs — real readings take over once there are a few weeks of them.' : ' An owner or equipment manager can add a usage estimate.'}
        </p>
      </div>
      {err && <div className="text-rust-700 text-sm">{err}</div>}
      <ul className="space-y-3">
        {need.map(m => {
          const tasks = unscheduled.filter(u => u.equipment_id === m.id).sort((a, b) => a.hours_remaining - b.hours_remaining);
          return (
            <li key={m.id} className="border-l-2 border-gold-500/50 pl-3 space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <Link to={`/equipment/${m.id}`} className="text-sm font-medium text-ink-900 hover:underline flex-1 min-w-[10rem]">{m.name}</Link>
                {canManage && (
                  <>
                    <input
                      className="input w-24" type="number" min="0" step="0.5" inputMode="decimal" placeholder="hr / week"
                      aria-label={`${m.name}: typical hours per week`}
                      value={vals[m.id] || ''} onChange={e => setVals(s => ({ ...s, [m.id]: e.target.value }))}
                    />
                    <button onClick={() => save(m)} disabled={busy === m.id || !vals[m.id]} className="btn-primary text-sm">
                      {busy === m.id ? '…' : 'Save'}
                    </button>
                  </>
                )}
              </div>
              <div className="text-xs text-ink-700/60">
                {tasks.slice(0, 3).map(t => `${t.description} in ${Math.round(t.hours_remaining)} hr`).join(' · ')}
                {tasks.length > 3 && ` · +${tasks.length - 3} more`}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// Subscribe link for the phone's own calendar app.
function PhoneFeed() {
  const [status, setStatus] = useState(null);
  const [link, setLink] = useState(null);   // { url, webcal_url } — only right after creating
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState('');

  async function load() {
    try { setStatus(await api.get('/api/calendar/feed')); } catch (e) { setErr(e.message); }
  }
  useEffect(() => { load(); }, []);

  async function create() {
    if (status?.active && !confirm('Make a new link? The old one stops working, so any calendar already subscribed to it will need the new link.')) return;
    setBusy(true); setErr(''); setCopied(false);
    try { setLink(await api.post('/api/calendar/feed')); await load(); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }
  async function revoke() {
    if (!confirm('Turn off your calendar link? Subscribed calendars will stop updating.')) return;
    try { await api.delete('/api/calendar/feed'); setLink(null); await load(); }
    catch (e) { setErr(e.message); }
  }
  async function copy() {
    try { await navigator.clipboard.writeText(link.url); setCopied(true); } catch {}
  }

  return (
    <section className="card space-y-3">
      <div>
        <h2 className="font-medium text-olive-900">Put it on your phone's calendar</h2>
        <p className="text-sm text-ink-700/70">
          Subscribe once and upcoming services appear in your iPhone or Google calendar, updating on their own as hour meters change.
        </p>
      </div>
      {err && <div className="text-rust-700 text-sm">{err}</div>}

      {link ? (
        <div className="bg-olive-50 rounded-lg p-3 space-y-3">
          <p className="text-sm text-ink-900">
            <strong>Your link — shown only this once.</strong> Anyone who has it can see the service schedule, so keep it to yourself.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <a href={link.webcal_url} className="btn-primary text-sm text-center">Add to this device's calendar</a>
            <button onClick={copy} className="btn-ghost text-sm">{copied ? '✓ Copied' : 'Copy link'}</button>
          </div>
          <input className="input text-xs" readOnly value={link.url} onFocus={e => e.target.select()} aria-label="Calendar link" />
          <ul className="text-xs text-ink-700/70 space-y-1 list-disc pl-4">
            <li><strong>iPhone / Mac:</strong> tap “Add to this device's calendar” and choose Subscribe.</li>
            <li><strong>Google Calendar:</strong> on a computer, Other calendars → + → From URL → paste the copied link.</li>
          </ul>
        </div>
      ) : (
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={create} disabled={busy} className="btn-primary text-sm">
            {busy ? '…' : status?.active ? 'Make a new link' : 'Create my calendar link'}
          </button>
          {status?.active && (
            <>
              <span className="text-xs text-ink-700/60">
                Link is on{status.last_fetched_at ? ' · a calendar last checked it recently' : ' · not used by a calendar yet'}
              </span>
              <button onClick={revoke} className="text-xs text-rust-700 hover:underline">turn off</button>
            </>
          )}
        </div>
      )}
    </section>
  );
}
